import React, { useRef, useState } from "react";
import { cx, useTranslations } from "@jugl-web/utils";
import { CropImageModal } from "@jugl-web/ui-components/web/AvatarSelect/components/CropImageModal/CropImageModal";
import {
  InteractiveContainer,
  LoadingAnimation,
  Menu,
} from "@jugl-web/ui-components";
import { ReactComponent as ImagePlaceholderIcon } from "./assets/image-placeholder.svg";
import { ReactComponent as RemoveIcon } from "./assets/remove.svg";
import { ReactComponent as ImageIcon } from "./assets/image.svg";

export type CompanyAvatarFieldProps = {
  onSave: (value?: File) => void;
  value: string | File | undefined;
};
export const CompanyAvatarField: React.FC<CompanyAvatarFieldProps> = ({
  onSave,
  value,
}) => {
  const $input = useRef<HTMLInputElement>(null);
  const [tempImage, setTempImage] = useState<File | undefined>(undefined);
  const [showCropImageModal, setShowCropImageModal] = useState<boolean>(false);
  const { t } = useTranslations();
  const handleInputChange: React.ChangeEventHandler<HTMLInputElement> = async (
    e
  ) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    e.target.value = "";
    setTempImage(file);
    setShowCropImageModal(true);
  };

  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const handleSaveImage = (image: File) => {
    onSave(image);
    setTempImage(image);
    handleCloseCropImageModal();
  };

  const handleFileSelect = async () => {
    $input.current?.click();
  };

  const handleCloseCropImageModal = () => setShowCropImageModal(false);

  const containerStyles = cx(
    "h-[176px] w-[176px] max-h-[176px] aspect-square rounded-full border border-solid border-grey-200 flex items-center justify-center"
  );

  return (
    <div className="flex w-max flex-col items-center">
      <input
        ref={$input}
        onChange={handleInputChange}
        type="file"
        accept="image/png, image/jpg, image/jpeg"
        name="image"
        hidden
      />
      {!value ? (
        <InteractiveContainer
          onClick={handleFileSelect}
          className={containerStyles}
        >
          <ImagePlaceholderIcon />
        </InteractiveContainer>
      ) : (
        <Menu
          placement="right"
          renderTrigger={({ Trigger, triggerRef }) => (
            <Trigger
              as={InteractiveContainer}
              ref={triggerRef}
              className={containerStyles}
            >
              <div className="border-grey-200 flex items-center justify-center overflow-hidden rounded-full border border-solid">
                <img
                  src={
                    typeof value === "string"
                      ? value
                      : URL.createObjectURL(value)
                  }
                  alt=""
                  className="h-full w-full"
                  onLoad={() => setIsImageLoaded(true)}
                  hidden={!isImageLoaded}
                />
                {!isImageLoaded && <LoadingAnimation size="lg" />}
              </div>
            </Trigger>
          )}
          sections={[
            [
              {
                id: "upload",
                icon: <ImageIcon />,
                label: t({
                  id: "avatar-select-component.upload-new-photo",
                  defaultMessage: "Upload New Photo",
                }),
                onSelect: (event, close) => {
                  event.stopPropagation();
                  close();
                  handleFileSelect();
                },
              },
              {
                id: "remove",
                icon: <RemoveIcon />,
                label: t({
                  id: "avatar-select-component.remove-picture",
                  defaultMessage: "Remove picture",
                }),
                onSelect: (event, close) => {
                  event.stopPropagation();
                  close();
                  onSave(undefined);
                  setTempImage(undefined);
                },
              },
            ],
          ]}
        />
      )}

      <div className="font-secondary mt-4 text-sm">Upload Company Logo</div>

      {showCropImageModal && tempImage && (
        <CropImageModal
          isOpen={showCropImageModal}
          image={tempImage}
          onClose={handleCloseCropImageModal}
          onSave={handleSaveImage}
          onChangeImage={handleFileSelect}
        />
      )}
    </div>
  );
};

export default CompanyAvatarField;
